import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Scale, Gauge, HeartHandshake } from "lucide-react";

const pillars = [
  {
    icon: Scale,
    title: "Tydligt innan start",
    desc: "Du ska förstå vad bilen behöver och ha en tydlig prisbild innan arbetet börjar.",
  },
  {
    icon: Gauge,
    title: "Felsökning med rätt verktyg",
    desc: "När en lampa tänds eller bilen beter sig annorlunda börjar vi med att ta reda på varför.",
  },
  {
    icon: ShieldCheck,
    title: "Omsorg om bilen",
    desc: "Varje jobb ska göras ordentligt, med fokus på funktion, säkerhet och ett bra slutresultat.",
  },
  {
    icon: HeartHandshake,
    title: "Personlig kontakt",
    desc: "Rak kommunikation och en tydlig dialog om vad bilen behöver och vad som händer härnäst.",
  },
];

export default function Trust() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const parallax = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      ref={ref}
      className="relative py-24 lg:py-36 bg-white overflow-hidden"
    >
      <div className="max-w-[1360px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Image */}
        <motion.div
          style={{ y: parallax }}
          className="lg:col-span-5 order-2 lg:order-1"
        >
          <div className="spotlight-frame clip-corner aspect-[4/5] w-full">
            <img
              src="https://images.unsplash.com/photo-1632823639409-ce060d5a54cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxjYXIlMjBtZWNoYW5pYyUyMHdvcmtzaG9wJTIwcmVwYWlyJTIwY2FyJTIwaGVhZGxpZ2h0JTIwYnJha2UlMjBkZXRhaWx8ZW58MHx8fHwxNzkxMTkyMzk2fDA&ixlib=rb-4.1.0&q=85"
              alt="Mekaniker utför detaljarbete"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>

        {/* Pillars */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
          >
            <span className="eyebrow">04 — Varför ZAM</span>
            <h2 className="hero-text mt-5 text-[9vw] sm:text-[6vw] lg:text-[4.4vw] xl:text-[64px]">
              Bilservice ska kännas<br />
              <span className="italic text-[#D52027]">tydlig hela vägen.</span>
            </h2>
          </motion.div>

          <div className="mt-12 grid sm:grid-cols-2 gap-x-10 gap-y-10">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                >
                  <div className="h-12 w-12 rounded-full border border-slate-200 bg-white grid place-items-center">
                    <Icon
                      className="h-5 w-5 text-[#1F3C88]"
                      strokeWidth={1.7}
                    />
                  </div>
                  <h3 className="mt-5 font-display text-[1.55rem] lg:text-[1.75rem] text-slate-900 tracking-tight leading-[1.05]">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-slate-600 text-[0.95rem] leading-relaxed">
                    {p.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
